import { Component, EventEmitter, forwardRef, OnDestroy, OnInit, Output, ɵɵgetComponentDepsFactory } from '@angular/core';
import { Location } from '@angular/common';

import { CompanyService } from 'src/app/_service/company.service';
import { getCompanyData, postCompanyData, companyForm, companyType } from 'src/app/_model/company';
import { TenantService } from 'src/app/_service/tenant.service';
import { postTenantData, tenantForm, rbac } from 'src/app/_model/tenant';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CompanyEditComponent } from '../company-edit/company-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { catchError, switchMap, throwError } from 'rxjs';

@Component({
  selector: 'app-company-register',
  templateUrl: './company-register.component.html',
  styleUrls: ['./company-register.component.css'],
})
export class CompanyRegisterComponent implements OnInit, OnDestroy {
  @Output() dataChange = new EventEmitter();
  form = new companyForm;
  companyForm = this.form.defaultForm;
  editCompanyData!: postCompanyData;
  editMode = false;
  isSubmitting = false;
  companyTypes: companyType[] = [];
  types: string[] = [];
  rids: number[] = [0, 1, 2];
  rbac: any[] = [];
  countryOptions: { value: string; label: string }[] = [];

  constructor(
    private location: Location,
    private companyService: CompanyService,
    private iconRegistry: MatIconRegistry,
    private tenantService: TenantService,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) { 
    this.translateService.setDefaultLang('en');
    this.iconRegistry.addSvgIcon(
    'company',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/company.svg')
    );
    this.iconRegistry.addSvgIcon(
    'zoomin',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/zoomin.svg')
    );
    this.iconRegistry.addSvgIcon(
      'zoom',
      sanitizer.bypassSecurityTrustResourceUrl('/assets/zoom.svg')
    );
  }

  ngOnInit(): void {
    if(this.companyService.editCompanyFlag){
      this.setEditCompanyData(this.companyService.editCompanyData);
      this.addToCompanyData(this.companyService.editCompanyData)
      this.editMode = true;
    }else{
      this.editMode = false;
      this.companyForm.patchValue({types: []});
    }
    this.companyTypes=[
      { value: 'owner', display: this.translateService.instant('owner') },
      { value: 'exowner', display: this.translateService.instant('exOwner') },
      { value: 'manager', display: this.translateService.instant('manager') },
      { value: 'exmanager', display: this.translateService.instant('exManagement') },
      { value: 'builder', display: this.translateService.instant('builder') },
      { value: 'manufacturer', display: this.translateService.instant('manufacturer') },
      { value: 'maintainer', display: this.translateService.instant('maintainer') }
    ];
    this.countryOptions = this.getCountryOptions();
  }

  private getCountryOptions(): { value: string; label: string }[] {
    const countries = [
      'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia','Australia','Austria',
      'Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi','Cabo Verde','Cambodia','Cameroon','Canada','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica','Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic','Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia','Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Kuwait','Kyrgyzstan','Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg','Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway','Oman','Pakistan','Palau','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania','Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia','Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia','Solomon Islands','Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka','Sudan','Suriname','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu','Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan','Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'
    ];

    return [
      { value: '', label: 'Select country' },
      ...countries.map((country) => ({ value: country, label: country }))
    ];
  }
  
  ngOnDestroy(): void {
    this.companyService.resetSelectedCompanyID();
    this.companyService.resetEditCompany();
    this.companyForm.reset();
  }

  setEditCompanyData(editCompanyData: postCompanyData){
    this.companyForm.patchValue(editCompanyData);
  }

  onSubmit() {
    this.companyForm.get('types')?.setValue(this.types);
    // console.log(this.companyForm.value);
    if(this.checkSubmit()){
      if(this.editMode){
        //update existing company
        this.companyService.submitExistingCompany(this.companyForm.value)
        .subscribe({
          error: (err) => {window.alert(this.translateService.instant("err"));},
          complete: () => {
            window.alert(this.translateService.instant("updated"));
            this.companyForm.reset();
            this.dataChange.emit();
          }
        })
      } else {
        // register new company
        this.isSubmitting = true;
        this.companyService.submitNewCompany(this.companyForm.value)
          .pipe(
            catchError((err) => {
              window.alert(this.translateService.instant("err"));
              return throwError(() => new Error("Failed to create company"));
            }),
            switchMap((newCompany: any) => {
              return this.registerTenant(newCompany?.id, newCompany?.name);
            })
          )
          .subscribe({
            next: async() => {
              window.alert(this.translateService.instant("companyAndTenantCreated"));
            },
            error: async(err) => {
              console.log("Error occurred: " + err)
            },
            complete: () => {
              this.location.back();
              this.isSubmitting= false;
              this.companyForm.reset();
              this.updateTenantList();
              this.dataChange.emit();
            }
          });
      }
    }
  }

  updateTenantList() {
    this.tenantService.tenantList = [];
  }

  registerTenant(company_id: string, name: string) {
    const currRbac: rbac[] = [{
      mgr: [company_id],
      vsl: [],
      rids: this.rids,
    }];
    const payload = {
      name: name + " default tenant",
      description: "Auto created default tenant for " + name,
      status_id: 1,
      rbac: currRbac,
    };
    return this.tenantService.createTenant(payload);
  }

  tempdata!:postCompanyData;
  addToCompanyData(data: any):any {
    this.tempdata = {
      name: data.name,
      otherName: data.otherName,
      ceo: data.ceo,
      address: data.address,
      addressLineTwo: data.addressLineTwo,
      city: data.city,
      zipCode: data.zipCode,
      telephone: data.telephone,
      fax: data.fax,
      email: data.email,
      thumbnailExtension: data.thumbnailExtension,
      businessContents: data.businessContents,
      rank: data.rank,
      country:data.country,
      types: data.types,
      typeOwner: data.typeOwner,
      typeManagement: data.typeManagement,
      typeBuilder: data.typeBuilder,
      typeManufacturer: data.typeManufacturer,
      typeMaintainer: data.typeMaintainer,
      typeExOwner: data.typeExOwner,
      typeExManagement: data.typeExManagement,
      isGlobalReadable: data.isGlobalReadable,
    };
    
  }
  Onclick(){
    // this.router.navigate([routing.UserEdit]);
    this.companyService.editCompanyFlag = true;
    this.companyService.editCompanyData = this.tempdata;  
    // this.router.navigate([routing.UserList]);

    this.dialog.open(CompanyEditComponent,{
      width:'744px',
      height: '85vh',
      // panelClass: 'fullscreen-dialog'
    })
  }
  onChange($event:any) {
    // console.log($event.target.value,$event.target.checked)
    let values = $event.target.value;
    let isChecked = $event.target.checked;
    let index = this.types.indexOf(values);
    if (isChecked && index === -1) {
      this.types.push(values);
    } else if (!isChecked && index !== -1) {
      this.types.splice(index, 1);
    }
  }

  onGlobalReadableChange($event: any) {
    this.companyForm.get('isGlobalReadable')?.setValue($event.target.checked);
  }

  onClose(){
    this.companyService.editCompanyFlag= false;
  }
  checkSubmit() {
    if(window.confirm('Can I register this?')){
      return true;
    }else{
      this.companyForm.get('types')?.setValue(this.types);
      return false;
    }
  }

  goBack(){
    this.location.back();
  }
}
